import { InsertSpeaker } from '@/connection/speakers/speakers';
import createUpdateGreenroomUserProfileMaskBody from './create-update-greenroom-user-profile-mask-body';
import { assertIsSpeaker } from '@/utils/assertions/assert-is-speaker';
import { Channel } from '@/types/channel.types';
import { Dispatch, SetStateAction } from 'react';
import { CreateSpeaker, Speaker } from '@/types/speakers.types';
import { GreenroomUserProfile } from '@/types/greenroom/profile.types';
import UpdateGreenroomProfileMask from '@/connection/greenroom/update-greenroom-profile';
import AttachGreenroomMask from '@/connection/greenroom/attach-greenroom-mask';
import { Meeting } from '@/types/greenroom/meeting.types';
import { addSpeakerToSpeakersSlice } from '@/store/slices/speakers';
import toastTrigger from '@ui/toast/toast-trigger';
import { useAppDispatch } from '@/store/hooks';
import { type StrippedBlAdmin } from '@/types/profile.types';
import { EToastTypes } from '@ui/toast/toast.types';

type Props = {
  userChannel: Channel | null;
  setSavingCast: Dispatch<SetStateAction<boolean>>;
  defaultSpeaker?: Speaker | null;
  workingSpeaker: Speaker | CreateSpeaker;
  baseLanguage: string;
  languages?: string[];
  greenroomProfile?: GreenroomUserProfile | null;
  meeting: Meeting | null;
  setGreenroomProfile: Dispatch<SetStateAction<GreenroomUserProfile | undefined>>;
  dispatch: ReturnType<typeof useAppDispatch>;
  handleNewCast?: (speaker: Speaker) => void;
  handleEditCast?: (speaker: Speaker) => void;
  channelAdmins: StrippedBlAdmin[];
};

const handleSaveSpeaker = async ({
  userChannel,
  defaultSpeaker,
  workingSpeaker,
  baseLanguage,
  languages,
  setSavingCast,
  greenroomProfile,
  meeting,
  setGreenroomProfile,
  dispatch,
  handleEditCast,
  handleNewCast,
  channelAdmins,
}: Props) => {
  if (!userChannel) return;

  try {
    setSavingCast(true);

    let newSpeaker: Speaker | undefined;
    let editSpeaker: Speaker | undefined;
    if (defaultSpeaker) {
      assertIsSpeaker(workingSpeaker);
      editSpeaker = { ...workingSpeaker };
    } else {
      newSpeaker = await InsertSpeaker(baseLanguage, languages || [baseLanguage], workingSpeaker);
    }

    if (greenroomProfile) {
      if (!workingSpeaker.email) throw new Error('Unable to update profile mask. No email address to update.');

      // if we have a greenroom profile, an invite was sent to the user. We need to update the greenroom profile with the rest of the
      const updateGreenroomUserProfilePostBody = createUpdateGreenroomUserProfileMaskBody({
        workingSpeaker,
        greenroomProfile,
        userChannel,
      });

      if (updateGreenroomUserProfilePostBody && meeting?.meeting) {
        //update the profile mask with the rest of the speaker details
        const updatedGreenroomProfileMask = await UpdateGreenroomProfileMask(
          meeting?.meeting,
          updateGreenroomUserProfilePostBody
        );
        const maskId = updatedGreenroomProfileMask?.entityValue?.id;

        if (!maskId) throw new Error('Failed to update profile mask. No maskId');

        // attach the mask to the invited user email
        await AttachGreenroomMask(meeting.meeting, maskId, workingSpeaker.email);

        setGreenroomProfile(undefined);
      }
    }

    // only add as new if we are not editing
    if (editSpeaker) {
      handleEditCast?.(editSpeaker);
    }
    // only add as new if we are not editing
    if (newSpeaker) {
      handleNewCast?.(newSpeaker);
      dispatch(
        addSpeakerToSpeakersSlice({
          ...newSpeaker,
          bl_admin: channelAdmins?.find((admin) => admin.email === newSpeaker.email) || null,
        })
      );
    }
  } catch (e) {
    console.error(e);
    toastTrigger('There was a problem saving the cast member', {
      type: EToastTypes.error,
      autoClose: 5000,
    });
  } finally {
    setSavingCast(false);
  }
};

export default handleSaveSpeaker;
